<template>
  <div class="input-panel panel-volume">
    <h2 class="input-panel__head"><template v-if="!customFacilityProfileExists">Annual </template>Volume</h2>

    <template v-if="customFacilityProfileExists">
      <ul class="panel-volume__custom-terms">
        <li class="panel-volume__custom-term" v-for="(term, index) in termValues"><b>{{ term.termLength }}-Month Term:</b> {{ formatNumberValue(term.usage, 0) }} kWh</li>
      </ul>

      <form class="panel-volume__form"
        v-show="stage === 5"
      >
        <div class="panel-volume__row panel-volume__row--center mrgn-btm-10">
          Include Adjustment?
        </div>

        <div class="panel-volume__row panel-volume__row--center">
          <button class="btn btn-sm"
            @click.prevent="allowBrokerFee(true)"
          >Yes</button>
          <button class="btn btn-sm"
            @click.prevent="declineBrokerFee(true)"
          >No</button>
        </div>
      </form>

      <form class="panel-volume__adjustment"
        v-show="stage === 6"
      >
        <div class="adjustment__headline">
          <label for="broker-fee">Adjustment:</label>
        </div>

        <div class="adjustment__inputs">
          <div class="inputs__amount">
            <div class="amount__inputs">
              <span class="inputs__prefix">$0.</span>
              <input 
                type="text" 
                ref="brokerFee"
                id="broker-fee"
                :value="brokerFee"
                @input="setLocalBrokerFee"
              >
              <span class="inputs__suffix">($/kWh)</span>
            </div>
            <button type="submit" class="amount__btn btn btn-sm"
              :disabled="!brokerFeeButtonActive"
              @click.prevent="setBrokerFee(true)"
            >Enter</button>
          </div>

          <div class="inputs__notes">
            <label for="adjustment-notes" class="visuallyhidden">Adjustment Notes</label>
            <input type="text" v-model="adjustmentNotes" id="adjustment-notes" placeholder="Add any notes here regarding the adjustment (optional)">
          </div>

          <div class="inputs__display-checkbox">
            <input type="checkbox" v-model="displayAdjustmentInNav" id="display-adjustment" :true-value="1" :false-value="0">
            <label for="display-adjustment" class="inline mrgn-left-5">Display Adjustment value with inputs at left.</label>
          </div>
        </div> <!-- .adjustment__inputs -->
      </form>
    </template>

    <template v-else>
      <form class="panel-volume__form"
        v-show="stage === 1"
      >
        <div class="panel-volume__row panel-volume__row--center">
          <fieldset>
            <div class="panel-volume__form__radioset">
              <label for="option-usage">
                <input type="radio" id="option-usage" name="volume-unit" value="usage"
                  @change="unitChange"
                  v-model="unit"
                >
                Usage (kWh/Year)
              </label>

              <fieldset class="panel-volume__form__fieldset"
                v-show="unit === 'usage'"
              >
                <div class="panel-volume__row panel-volume__row--center">
                  <label for="usage" class="visuallyhidden">Usage <small>(kWh/Year)</small></label>
                  <input type="text" name="usage" id="usage" ref="usage" class="mrgn-right-15" 
                    :value="annualUsage"
                    @input="setLocalUsage"
                  >
                  <button type="submit" class="btn btn-sm" 
                    :disabled="!volumeButtonActive"
                    @click.prevent="setVolume"
                  >Enter</button>
                </div>
              </fieldset>
            </div>

            <div class="panel-volume__form__radioset">
              <label for="option-total-cost">
                <input type="radio" id="option-total-cost" name="volume-unit" value="total-cost"
                  @change="unitChange"
                  v-model="unit"
                >
                Total Cost ($/Year)
              </label>

              <fieldset class="panel-volume__form__fieldset"
                v-show="unit === 'total-cost'"
              >
                <div class="panel-volume__row panel-volume__row--center">
                  <label for="total-cost" class="visuallyhidden">Total Cost <small>($/Year)</small></label>
                  <input type="text" name="total-cost" id="total-cost" ref="totalCost" class="mrgn-right-15"
                    :value="annualCost"
                    @input="setLocalCost"
                  >
                  <button type="submit" class="btn btn-sm" 
                    :disabled="!volumeButtonActive"
                    @click.prevent="setVolume"
                  >Enter</button>
                </div>
              </fieldset>
            </div>

            <div class="panel-volume__form__radioset">
              <label for="option-peak-demand">
                <input type="radio" id="option-peak-demand" name="volume-unit" value="annual-peak"
                  @change="unitChange"
                  v-model="unit"
                >
                Annual Peak (KW)
              </label>

              <fieldset class="panel-volume__form__fieldset"
                v-show="unit === 'annual-peak'"
              > 
                <div class="panel-volume__row panel-volume__row--center">
                  <label for="peak-demand" class="visuallyhidden">Annual Peak Demand <small>(KW)</small></label>
                  <input type="text" name="peak-demand" id="peak-demand" ref="peakDemand" class="mrgn-right-15"
                    :value="annualPeakDemand"
                    @input="setLocalPeakDemand"
                  >
                  <button type="submit" class="btn btn-sm" 
                    :disabled="!volumeButtonActive"
                    @click.prevent="setVolume"
                  >Enter</button>
                </div>
              </fieldset>
            </div>
          </fieldset>
        </div>
      </form>

      <form class="panel-volume__form"
        v-show="stage === 2"
      >
        <div class="panel-volume__row panel-volume__row--center mrgn-btm-10">
          Include Adjustment?
        </div>

        <div class="panel-volume__row panel-volume__row--center">
          <button class="btn btn-sm"
            @click.prevent="allowBrokerFee(false)"
          >Yes</button>
          <button class="btn btn-sm"
            @click.prevent="declineBrokerFee(false)"
          >No</button>
        </div>
      </form>

      <form class="panel-volume__adjustment"
        v-show="stage === 3"
      >
        <div class="adjustment__headline">
          <label for="broker-fee">Adjustment:</label>
        </div>

        <div class="adjustment__inputs">
          <div class="inputs__amount">
            <div class="amount__inputs">
              <span class="inputs__prefix">$0.</span>
              <input 
                type="text" 
                ref="brokerFee"
                id="broker-fee"
                :value="brokerFee"
                @input="setLocalBrokerFee"
              >
              <span class="inputs__suffix">($/kWh)</span>
            </div>
            <button type="submit" class="amount__btn btn btn-sm"
              :disabled="!brokerFeeButtonActive"
              @click.prevent="setBrokerFee(false)"
            >Enter</button>
          </div>

          <div class="inputs__notes">
            <label for="adjustment-notes" class="visuallyhidden">Adjustment Notes</label>
            <input 
              type="text" 
              v-model="adjustmentNotes" 
              id="adjustment-notes" 
              placeholder="Add any notes here regarding the adjustment (optional)"
            >
          </div>

          <div class="inputs__display-checkbox">
            <input 
              type="checkbox" 
              v-model="displayAdjustmentInNav" 
              id="display-adjustment" 
              :true-value="1" 
              :false-value="0"
            >
            <label for="display-adjustment" class="inline mrgn-left-5">Display Adjustment value with inputs at left.</label>
          </div>
        </div> <!-- .adjustment__inputs -->
      </form>

    </template>

  </div>
</template>

<script>
import { formatCurrency, formatNumber } from '../../../../common/helpers'

export default {
  data() {
    return {
      annualUsage: '',
      annualCost: '',
      annualPeakDemand: '',
      unit: 'usage',
      maxValue: 10000000000000,
      usageMin: 3000,
      usageMax: 4000000000,
      costMin: 1000,
      costMax: 200000000,
      peakDemandMin: 1,
      peakDemandMax: 500000,
      maxBrokerFeeDecimalPlaces: 6,
      brokerFee: '',
      adjustmentNotes: '',
      displayAdjustmentInNav: false,
      stage: 1,
      termsOfUse: window.epb.termsOfUse || [],
    }
  },
  computed: {
    currentPricingRequest() {
      return this.$store.getters.currentPricingRequest
    },
    acceptTermsOfUse() {
      return this.$store.getters.acceptTermsOfUse
    },
    volume() {
      return this.$store.getters.pricingRequestVolume
    },
    utility() {
      return this.$store.getters.utility
    },
    customFacilityProfile() {
      return this.$store.getters.customFacilityProfile
    },
    customFacilityProfileExists() {
      let count =  Object.keys(this.customFacilityProfile).length
      return count > 0
    },
    customFacilityVolume() {
      return this.$store.getters.customFacilityVolume
    },
    termLength() {
      return this.$store.getters.termLength
    },
    activeTerms() {
      var activeTerms = this.termLength
      activeTerms = activeTerms.filter(function(item) {
        return item.value != ''
      })
      return activeTerms
    },
    termValues() {
      let values = []

      for (let i = 0; i < this.activeTerms.length; ++i) {
        // look at each term length (12, 24, etc)

        values[i] = { 
          usage: 0,
          termLength: this.activeTerms[i].value
        }

        for (let n = 0; n < this.activeTerms[i].value; ++n) {
          // go through this loop once for each digit in the term length we're looking at, so 12 times for a 12 month term
          // take the current months usage, and add it to the value total
          if (this.customFacilityProfile.months[n]) {
            values[i].usage += this.customFacilityProfile.months[n].usage
          }
        }
      }

      return values
    },
    brokerFeeFormatted() {
      if (this.brokerFee) {
        return '0.' + this.brokerFee
      }
    },
    volumeButtonActive() {

      if (this.volume.amount) {
        // If a store value is set: 
        return true

      } else {
        // If no store value is set: 
        switch(this.unit) {
          case 'usage':
            if (this.annualUsage) {
              return true
            }
            break
          case 'total-cost':
            if (this.annualCost) {
              return true
            }
            break
          case 'annual-peak':
            if (this.annualPeakDemand) {
              return true
            }
            break
        }
      }
    },
    brokerFeeButtonActive() {
      if (!this.volume.brokerFee) { 

        if (this.brokerFee && (this.brokerFee.replace('$','') * 1) > 0) {
          return true
        }

        return false

      } else {
        
        if (this.brokerFee === this.volume.brokerFee) {
          return false
        }

        return true 
      }
    },
    brokerFeeDeclined() {
      return this.$store.getters.declineBrokerFee
    },
    submitPricingRequestHover() {
      return this.$store.getters.submitPricingRequestHover
    },
    pricingRequestStatus() {
      return this.$store.getters.pricingRequestStatus
    },
    acceptedTermsOfUse() {
      return this.acceptTermsOfUse == true
    },
    userIsLoggedIn() {
      return this.$store.state.auth.user.id && 
             this.$store.state.auth.plan.slug != 'free'
    }
  },
  methods: {
    unitChange() {
      this.$store.dispatch('clearVolume')
      this.clearAnnualUsage()
      this.clearAnnualCost()
      this.clearAnnualPeakDemand()
      this.clearBrokerFee()
      this.$store.dispatch('disablePricingRequestSubmission')
    },
    setVolume() {
      switch(this.unit) {
        case 'usage':
          let usageValue = this.annualUsage.replace(/\D/g,'')

          if (usageValue != this.volume.amount) {
            if (usageValue >= this.usageMin && usageValue <= this.usageMax) {
              this.$store.dispatch('setVolume', {
                amount: this.annualUsage.replace(/\D/g,''),
                unit: this.unit,
                brokerFee: this.brokerFeeFormatted
              })
              this.goToStage(2)
            } else {
              this.$store.dispatch('setNotification', { 
                type: 'error', 
                msg: 'Please enter a usage value between ' + formatNumber(this.usageMin, 0) + ' and ' + formatNumber(this.usageMax, 0) + ' kWh/Year.' 
              })
            }
          } else {
            this.goToStage(2)
          }

          break

        case 'total-cost':
          let costValue = this.annualCost.replace(/\D/g,'')

          if (costValue != this.volume.amount) {
            if (costValue >= this.costMin && costValue <= this.costMax) {
              this.$store.dispatch('setVolume', {
                amount: this.annualCost.replace(/\D/g,''),
                unit: this.unit,
                brokerFee: this.brokerFeeFormatted
              })
              this.goToStage(2)
            } else {
              this.$store.dispatch('setNotification', { 
                type: 'error', 
                msg: 'Please enter a total cost between ' + formatCurrency(this.costMin, 0) + ' and ' + formatCurrency(this.costMax, 0) + '' 
              })
            }
          } else {
            this.goToStage(2)
          }

          break

        case 'annual-peak':
          let peakDemandValue = this.annualPeakDemand.replace(/\D/g,'')

          if (usageValue != this.volume.amount) {
            if (peakDemandValue >= this.peakDemandMin && peakDemandValue <= this.peakDemandMax) {
              this.$store.dispatch('setVolume', {
                amount: this.annualPeakDemand.replace(/\D/g,''),
                unit: this.unit,
                brokerFee: this.brokerFeeFormatted
              })
              this.goToStage(2)
            } else {
              this.$store.dispatch('setNotification', { 
                type: 'error', 
                msg: 'Please enter a peak demand value between ' + formatNumber(this.peakDemandMin, 0) + ' and ' + formatNumber(this.peakDemandMax, 0) + ' KW.' 
              })
            }
          } else {
            this.goToStage(2)
          }

          break
      }
    },
    setBrokerFee(customFacilityProfile) {

      // Custom profile...
      if (customFacilityProfile) {
        this.$store.dispatch('setCustomFacilityVolume', {
          terms: this.termValues,
          brokerFee: this.brokerFeeFormatted,
          adjustmentNotes: this.adjustmentNotes,
          displayAdjustmentInNav: this.displayAdjustmentInNav,
        })

        this.$router.push({ name: 'GetPricing' })

      // ... or non-custom profile.
      } else {

        if (this.volume.amount && this.volume.unit) { 
          this.$store.dispatch('setVolume', {
            amount: this.volume.amount,
            unit: this.volume.unit,
            brokerFee: this.brokerFeeFormatted,
            adjustmentNotes: this.adjustmentNotes,
            displayAdjustmentInNav: this.displayAdjustmentInNav,
          })

          this.$router.push({ name: 'GetPricing' })
        }
      }
    },
    goToStage(stage) {
      this.stage = stage
    },
    setLocalUsage() {
      this.clearAnnualCost()
      this.clearAnnualPeakDemand()
      this.unit = 'usage'

      let value = event.target.value.replace(/\D/g,'')
      this.$refs.usage.value = formatNumber(value, 0)
      this.annualUsage = formatNumber(value, 0)
    },
    setLocalCost() {
      this.clearAnnualUsage()
      this.clearAnnualPeakDemand()
      this.unit = 'total-cost'

      let value = event.target.value.replace(/\D/g,'')
      this.$refs.totalCost.value = formatCurrency(value, 0)
      this.annualCost = formatCurrency(value, 0)
    },
    setLocalPeakDemand() {
      this.clearAnnualCost()
      this.clearAnnualUsage()
      this.unit = 'annual-peak'

      let value = event.target.value.replace(/\D/g,'')
      this.$refs.peakDemand.value = formatNumber(value, 0)
      this.annualPeakDemand = formatNumber(value, 0)
    },
    allowBrokerFee(customFacilityProfile) {
      this.$store.dispatch('allowBrokerFee')

      if (customFacilityProfile) {
        this.goToStage(6)
      } else {
        this.goToStage(3)
      }
    },
    declineBrokerFee(customFacilityProfile) {

      // The following condition was commented-out on 11/20/2023
      // since I, Taylor, don't know its purpose, and it was preventing
      // the Volume from being displayed for custom profiles in certain situations.
      // If it is later determined that we do in fact need the below condition, 
      // then I *think* the solution would be to make sure that `this.brokerFeeDeclined` 
      // (`this.$store.getters.declineBrokerFee`) is reset whenever this component 
      // (Volume.vue) is mounted.
      // 
      // @see https://app.asana.com/0/0/1204229200521353/1205993522233305/f

      // if (this.brokerFeeDeclined) {
      //   this.$router.push({ name: 'GetPricing' })

      // } else {

        if (customFacilityProfile) {
          this.$store.dispatch('setCustomFacilityVolume', {
            terms: this.termValues,
            brokerFee: ''
          })

          this.$store.dispatch('declineBrokerFee')
          this.$router.push({ name: 'GetPricing' })

        } else {

          if (this.volume.brokerFee) {
            this.$store.dispatch('setVolume', {
              amount: this.volume.amount,
              unit: this.volume.unit,
              brokerFee: ''
            })
          }

          this.clearBrokerFee()
          this.$store.dispatch('declineBrokerFee')
          this.$router.push({ name: 'GetPricing' })
        }
      // }
    },
    setLocalBrokerFee(event) {
      let value = event.target.value.replace(/\D/g,'')

      if (value.length <= this.maxBrokerFeeDecimalPlaces) {
        this.$refs.brokerFee.value = value
        this.brokerFee = value
      } else {
        this.$refs.brokerFee.value = this.brokerFee
      }
    },
    clearAnnualUsage() {
      this.annualUsage = ''
    },
    clearAnnualCost() {
      this.annualCost = ''
    },
    clearAnnualPeakDemand() {
      this.annualPeakDemand = ''
    },
    clearBrokerFee() {
      this.brokerFee = ''
    },
    penultimateStage() {
      if (this.customFacilityProfileExists) {
        if (this.volume.brokerFee) {
          this.goToStage(6)
        } else {
          this.goToStage(5)
        }
      } else {
        if (this.volume.brokerFee) {
          this.goToStage(3)
        } else {
          this.goToStage(2)
        }
      }
    },
    hoverOnSubmitPricingRequest() {
      this.$store.dispatch('hoverOnSubmitPricingRequest')
    },
    hoverOffSubmitPricingRequest() {
      this.$store.dispatch('hoverOffSubmitPricingRequest')
    },
    focusPricingButton() {
      this.$nextTick(() => this.$refs.pricingbutton.focus())
    },
    getPricing() {
      $('#btn-get-pricing').trigger('click');
    },
    toggleTermsOfUse(e) {
      if (e.target.checked) {
        this.$store.dispatch('acceptTermsOfUse')
      } else {
        this.$store.dispatch('declineTermsOfUse')
      }
    },
    showTermsOfUse(type) {
      document.getElementById('modal-' + type).classList.add('active')
    },
    hideTermsOfUse(type) {
      document.getElementById('modal-' + type).classList.remove('active')
    },
    printTermsOfUse(type) {
      frames[type].print();
    },
    formatNumberValue(val, decimals) {
      return formatNumber(val, decimals)
    }
  },
  mounted() {

    // Custom profile...
    if (this.customFacilityProfileExists) {

      if (this.customFacilityVolume.brokerFee) {
        this.brokerFee = this.customFacilityVolume.brokerFee.toString().split('.')[1]
      }

      // Adjustment notes.
      this.adjustmentNotes = this.customFacilityVolume.adjustmentNotes
      this.displayAdjustmentInNav = this.customFacilityVolume.displayAdjustmentInNav

      this.goToStage(5)

    // ... or non-custom profile.
    } else if (this.volume.amount && this.volume.unit) {

      // Adjustment notes.
      this.adjustmentNotes = this.volume.adjustmentNotes
      this.displayAdjustmentInNav = this.volume.displayAdjustmentInNav

      this.unit = this.volume.unit
      this.goToStage(1)

      switch(this.volume.unit) {
        case 'usage':
          this.annualUsage = formatNumber(this.volume.amount, 0)
          break

        case 'total-cost':
          this.annualCost = formatCurrency(this.volume.amount, 0)
          break

        case 'annual-peak':
          this.annualPeakDemand = formatNumber(this.volume.amount, '', 0)
          break

        default:
          if (!this.customFacilityProfile) {
            this.$refs.usage.focus() 
          }
      }

      if (this.volume.brokerFee) {
        this.brokerFee = this.volume.brokerFee.toString().split('.')[1]
        this.goToStage(1)
      }

      if (this.volume.declineBrokerFee) {
        this.goToStage(1)
      }
    }
  }
}
</script>
